import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swipeable from "@/components/Swipeable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { resolveColor } from "../utils/colors";
import {
  HeartPulse,
  Plus,
  ArrowLeft,
  Search,
  Loader2,
  Settings,
  Trash2,
  X,
  Check,
  Pencil,
  Home,
  IndianRupee,
  BarChart3,
  Building2,
  BadgeCheck,
  Hospital,
  FileText,
  FileUp,
  Sparkles,
} from "lucide-react";
import { authGet, authPost, authPut, authDelete, authUpload, getAuthToken, isAdvisor, getUserRole } from "../utils/auth";
import MobileBottomNav from "../components/MobileBottomNav";
import MobileSidebar from "../components/MobileSidebar";

const API = process.env.REACT_APP_BACKEND_URL;

const getContrastTextColor = (hexColor) => {
  if (!hexColor) return "#ffffff";
  let hex = hexColor.replace("#", "");
  if (!/^[0-9A-Fa-f]{6}$/.test(hex) && !/^[0-9A-Fa-f]{3}$/.test(hex)) return "#ffffff";
  if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#1e293b" : "#ffffff";
};

const fmtAmount = (v) => {
  const n = parseFloat(String(v).replace(/[^0-9.]/g, ""));
  if (!v || isNaN(n)) return "—";
  return "₹" + n.toLocaleString("en-IN");
};

const fmtINR = (n) => "₹" + Math.round(Number(n) || 0).toLocaleString("en-IN");

// Classify a claim stage into a lifecycle bucket: active | approved | rejected.
// Approved & Rejected are "inactive" (claim is closed); everything else is "active".
const APPROVED_STAGE_RE = /approv|settl|paid/i;
const REJECTED_STAGE_RE = /reject|declin|denied/i;
const classifyStage = (stage) => {
  if (!stage) return "active";
  const hay = `${stage.id || ""} ${stage.name || ""}`;
  if (APPROVED_STAGE_RE.test(hay)) return "approved";
  if (REJECTED_STAGE_RE.test(hay)) return "rejected";
  return "active";
};

const CLAIM_TABS = [
  { id: "active", label: "Active", activeCls: "bg-blue-500 text-white", badgeCls: "bg-blue-400/30" },
  { id: "approved", label: "Approved", activeCls: "bg-emerald-500 text-white", badgeCls: "bg-emerald-400/30" },
  { id: "rejected", label: "Rejected", activeCls: "bg-red-500 text-white", badgeCls: "bg-red-400/30" },
];

const AnalyticsSection = ({ title, rows, total }) => (
  <div>
    <h4 className="text-sm font-semibold text-[#1B2A4A] mb-2">{title}</h4>
    <div className="space-y-1.5">
      {rows.length === 0 && <p className="text-xs text-[#3A4557]">No data</p>}
      {rows.map((r, i) => {
        const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
        return (
          <div key={i} className="rounded-lg border border-[#C9D4E6] bg-white p-2.5" data-testid={`analytics-row-${title.replace(/\s+/g, "-").toLowerCase()}-${i}`}>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 text-[#1B2A4A] font-medium">
                {r.color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: r.color }} />}
                {r.name}
              </span>
              <span className="text-[#3A4557] text-xs">{r.count} claim{r.count === 1 ? "" : "s"}</span>
            </div>
            <div className="h-1.5 rounded-full bg-[#EDF1F8] mt-1.5 overflow-hidden">
              <div className="h-full bg-[#A87608]" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between mt-1 text-[11px]">
              <span className="text-[#A87608]">Claimed: {fmtINR(r.claim_amount)}</span>
              <span className="text-emerald-700 font-medium">Settled: {fmtINR(r.settled_amount)}</span>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const EMPTY_CLAIM = {
  claimant_name: "",
  client_id: "",
  policy_id: "",
  policy_number: "",
  insurer: "",
  broker: "",
  claim_amount: "",
  settled_amount: "",
  claim_type: "",
  hospitalization_type: "",
  stage: "",
  hospital_name: "",
  claim_ref_no: "",
  admission_date: "",
  discharge_date: "",
  notes: "",
};

export default function ClaimDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [stages, setStages] = useState([]);
  const [claimTypes, setClaimTypes] = useState([]);
  const [hospTypes, setHospTypes] = useState([]);
  const [lookup, setLookup] = useState({ entries: [], brokers: [] });

  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsFy, setAnalyticsFy] = useState("");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [claimTab, setClaimTab] = useState("active"); // active | approved | rejected
  const [stageFilter, setStageFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showSidebar, setShowSidebar] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_CLAIM);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [showNameSuggest, setShowNameSuggest] = useState(false);
  const nameBoxRef = useRef(null);

  const [showSettings, setShowSettings] = useState(false);
  const [newStageName, setNewStageName] = useState("");
  const [newStageColor, setNewStageColor] = useState("#3b82f6");
  const [newType, setNewType] = useState("");
  const [newHosp, setNewHosp] = useState("");
  const [editingStageId, setEditingStageId] = useState(null);
  const [editStageName, setEditStageName] = useState("");
  const [editStageColor, setEditStageColor] = useState("#3b82f6");

  const canManage = isAdvisor();
  const userRole = getUserRole();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [c, s, t, h, l] = await Promise.all([
        authGet("/api/advisor/claims"),
        authGet("/api/advisor/claims/stages"),
        authGet("/api/advisor/claims/types"),
        authGet("/api/advisor/claims/hosp-types"),
        authGet("/api/advisor/claims/lookup"),
      ]);
      if (c.ok) setClaims(c.data.claims || []);
      if (s.ok) setStages((s.data.stages || []).map((x) => ({ ...x, color: resolveColor(x.color) })));
      if (t.ok) setClaimTypes(t.data.types || []);
      if (h.ok) setHospTypes(h.data.types || []);
      if (l.ok) setLookup({ entries: l.data.entries || [], brokers: l.data.brokers || [] });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getStage = (id) => stages.find((s) => s.id === id) || stages[0] || { name: id, color: "#64748b" };

  const loadAnalytics = async (fy) => {
    setAnalyticsLoading(true);
    const res = await authGet(`/api/advisor/claims/analytics${fy ? `?fy=${fy}` : ""}`);
    if (res.ok) { setAnalytics(res.data); setAnalyticsFy(res.data.selected_fy); }
    setAnalyticsLoading(false);
  };
  const openAnalytics = () => { setShowAnalytics(true); loadAnalytics(analyticsFy); };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...EMPTY_CLAIM, stage: stages[0]?.id || "", claim_type: claimTypes[0]?.name || "", hospitalization_type: hospTypes[0]?.name || "" });
    setShowModal(true);
  };

  const openEdit = (claim) => {
    setEditingId(claim.id);
    setForm({ ...EMPTY_CLAIM, ...claim });
    setShowModal(true);
  };

  const pickSuggestion = (e) => {
    setForm((prev) => ({
      ...prev,
      claimant_name: e.name,
      client_id: e.client_id || "",
      policy_id: e.policy_id || "",
      policy_number: e.policy_number || prev.policy_number,
      broker: e.broker || prev.broker,
      insurer: e.insurer || prev.insurer,
    }));
    setShowNameSuggest(false);
  };

  const handleExtractLetter = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) { toast.error("Upload a PDF or image (JPG/PNG)"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Keep the file under 20 MB"); return; }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await authUpload("/api/advisor/claims/extract", fd);
      if (res.ok && res.data?.extracted_data) {
        const d = res.data.extracted_data;
        const clean = Object.fromEntries(Object.entries(d).filter(([, v]) => v !== null && v !== "" && v !== undefined));
        setForm((prev) => ({ ...prev, ...clean }));
        toast.success("Details extracted — please review and save");
      } else {
        toast.error(res.data?.detail || "Could not read the letter. Please try another file.");
      }
    } catch {
      toast.error("Could not read the letter. Please try again.");
    } finally {
      setExtracting(false);
    }
  };

  const handleViewLetter = async () => {
    if (!editingId) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${API}/api/advisor/claims/${editingId}/document`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) { toast.error("Document not available"); return; }
      const blob = await res.blob();
      window.open(window.URL.createObjectURL(blob), "_blank");
    } catch {
      toast.error("Could not open the document");
    }
  };

  const handleSave = async () => {    if (!form.claimant_name.trim()) {
      toast.error("Claimant name is required");
      return;
    }
    setSaving(true);
    try {
      const res = editingId
        ? await authPut(`/api/advisor/claims/${editingId}`, form)
        : await authPost("/api/advisor/claims", form);
      if (res.ok) {
        toast.success(editingId ? "Claim updated" : "Claim added");
        setShowModal(false);
        setEditingId(null);
        fetchAll();
      } else {
        throw new Error(res.data?.detail || "Failed to save claim");
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    if (!window.confirm("Delete this claim permanently?")) return;
    setSaving(true);
    const res = await authDelete(`/api/advisor/claims/${editingId}`);
    if (res.ok) {
      toast.success("Claim deleted");
      setShowModal(false);
      setEditingId(null);
      fetchAll();
    } else {
      toast.error(res.data?.detail || "Failed to delete claim");
    }
    setSaving(false);
  };

  const handleStageChange = async (claimId, stageId) => {
    const res = await authPut(`/api/advisor/claims/${claimId}`, { stage: stageId });
    if (res.ok) {
      setClaims((prev) => prev.map((c) => (c.id === claimId ? { ...c, stage: stageId } : c)));
      const st = stages.find((s) => s.id === stageId);
      const cat = classifyStage(st);
      if (cat === "approved") toast.success("Claim approved — moved to Approved");
      else if (cat === "rejected") toast.success("Claim rejected — moved to Rejected");
      else toast.success("Stage updated");
      const claim = claims.find((c) => c.id === claimId);
      if (cat === "approved" && claim && !claim.settled_amount) {
        openEdit({ ...claim, stage: stageId });
        toast("💰 Please enter the settled amount for this approved claim");
      }
    } else toast.error("Failed to update stage");
  };

  // ---- Settings CRUD ----
  const addStage = async () => {
    if (!newStageName.trim()) return;
    const res = await authPost("/api/advisor/claims/stages", { name: newStageName, color: newStageColor, order: stages.length + 1 });
    if (res.ok) {
      setStages([...stages, { ...res.data.stage, color: resolveColor(res.data.stage.color) }]);
      setNewStageName("");
      setNewStageColor("#3b82f6");
      toast.success("Stage added");
    }
  };
  const saveStageEdit = async (id) => {
    if (!editStageName.trim()) return toast.error("Name required");
    const res = await authPut(`/api/advisor/claims/stages/${id}`, { name: editStageName, color: editStageColor });
    if (res.ok) {
      setStages(stages.map((s) => (s.id === id ? { ...s, name: editStageName, color: resolveColor(editStageColor) } : s)));
      setEditingStageId(null);
      toast.success("Stage updated");
    } else toast.error(res.data?.detail || "Failed");
  };
  const deleteStage = async (id) => {
    if (stages.length <= 1) return toast.error("You need at least 1 stage");
    if (!window.confirm("Delete this stage? Claims on it move to the first stage.")) return;
    const res = await authDelete(`/api/advisor/claims/stages/${id}`);
    if (res.ok) {
      const moved = res.data?.moved_claims || 0;
      toast.success(moved > 0 ? `Stage removed · ${moved} claim(s) moved` : "Stage removed");
      fetchAll();
    } else toast.error(res.data?.detail || "Failed to remove stage");
  };
  const addType = async () => {
    if (!newType.trim()) return;
    const res = await authPost("/api/advisor/claims/types", { name: newType, order: claimTypes.length + 1 });
    if (res.ok) { setClaimTypes([...claimTypes, res.data.type]); setNewType(""); toast.success("Claim type added"); }
  };
  const deleteType = async (id) => {
    const res = await authDelete(`/api/advisor/claims/types/${id}`);
    if (res.ok) setClaimTypes(claimTypes.filter((t) => t.id !== id));
    else toast.error(res.data?.detail || "Failed");
  };
  const addHosp = async () => {
    if (!newHosp.trim()) return;
    const res = await authPost("/api/advisor/claims/hosp-types", { name: newHosp, order: hospTypes.length + 1 });
    if (res.ok) { setHospTypes([...hospTypes, res.data.type]); setNewHosp(""); toast.success("Hospitalization type added"); }
  };
  const deleteHosp = async (id) => {
    const res = await authDelete(`/api/advisor/claims/hosp-types/${id}`);
    if (res.ok) setHospTypes(hospTypes.filter((t) => t.id !== id));
    else toast.error(res.data?.detail || "Failed");
  };

  const nameMatches = form.claimant_name
    ? lookup.entries.filter((e) => e.name.toLowerCase().includes(form.claimant_name.toLowerCase())).slice(0, 8)
    : lookup.entries.slice(0, 8);

  const claimCategory = (c) => classifyStage(getStage(c.stage));
  const tabCounts = {
    active: claims.filter((c) => claimCategory(c) === "active").length,
    approved: claims.filter((c) => claimCategory(c) === "approved").length,
    rejected: claims.filter((c) => claimCategory(c) === "rejected").length,
  };
  const visibleStages = stages.filter((s) => classifyStage(s) === claimTab);

  const filtered = claims.filter((c) => {
    if (claimCategory(c) !== claimTab) return false;
    if (stageFilter !== "all" && c.stage !== stageFilter) return false;
    if (typeFilter !== "all" && c.claim_type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(c.claimant_name?.toLowerCase().includes(q) || c.policy_number?.includes(search) || c.broker?.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const countByStage = (id) => claims.filter((c) => c.stage === id).length;
  const switchTab = (tab) => { setClaimTab(tab); setStageFilter("all"); };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] via-[#E7EDF6] to-[#EDF1F8] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#A87608]" />
      </div>
    );
  }

  const inputCls = "bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] via-[#E7EDF6] to-[#EDF1F8] flex flex-col">
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#C9D4E6] sticky top-0 z-40 shrink-0">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/advisor/dashboard")} className="p-2 -ml-2 rounded-lg hover:bg-[#EDF1F8]" data-testid="claims-back-btn">
              <ArrowLeft className="h-5 w-5 text-[#1B2A4A]" />
            </button>
            <button onClick={() => navigate("/advisor/dashboard")} className="hidden md:flex p-2 rounded-lg hover:bg-[#EDF1F8]" title="Home">
              <Home className="h-5 w-5 text-[#3A4557]" />
            </button>
            <div className="h-9 w-9 rounded-lg bg-green-500/15 flex items-center justify-center">
              <Hospital className="h-5 w-5 text-green-600" />
            </div>
            <h1 className="text-base md:text-lg font-bold text-[#1B2A4A]">Claims</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={openAnalytics} className="border-[#C9D4E6] text-[#3A4557]" data-testid="claims-analytics-btn">
              <BarChart3 className="h-4 w-4 md:mr-1" /><span className="hidden md:inline">Analytics</span>
            </Button>
            {canManage && (
              <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} className="border-[#C9D4E6] text-[#3A4557]" data-testid="claims-settings-btn">
                <Settings className="h-4 w-4" />
              </Button>
            )}
            <Button size="sm" onClick={openAdd} className="bg-green-600 hover:bg-green-700 text-white" data-testid="add-claim-btn">
              <Plus className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">Add Claim</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-3 md:p-4 pb-28 md:pb-6">
        {/* Active / Approved / Rejected toggle */}
        <div className="flex items-center gap-1.5 bg-white/70 border border-[#C9D4E6] rounded-xl p-1 mb-3" data-testid="claim-lifecycle-tabs">
          {CLAIM_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${claimTab === t.id ? t.activeCls : "text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#E2E9F4]"}`}
              data-testid={`claim-tab-${t.id}`}
            >
              {t.label}
              <span className={`px-1.5 py-0.5 rounded text-xs ${claimTab === t.id ? t.badgeCls : "bg-[#E2E9F4] text-[#44506B]"}`}>{tabCounts[t.id]}</span>
            </button>
          ))}
        </div>

        {/* Claim type filter */}
        <Swipeable outerClassName="mb-3" className="flex items-center gap-2 pb-1 -mx-3 px-3 md:mx-0 md:px-0" testId="claim-type-filter">
          <span className="text-[#3A4557] text-xs shrink-0">Type:</span>
          <button onClick={() => setTypeFilter("all")}
            className={`px-2.5 py-1 rounded-full text-xs font-medium shrink-0 border ${typeFilter === "all" ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "bg-[#EDF1F8]/60 text-[#3A4557] border-transparent"}`}
            data-testid="claim-type-all">All</button>
          {claimTypes.map((t) => (
            <button key={t.id} onClick={() => setTypeFilter(typeFilter === t.name ? "all" : t.name)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium shrink-0 border ${typeFilter === t.name ? "bg-emerald-500 text-white border-emerald-500" : "bg-[#EDF1F8]/60 text-[#3A4557] border-transparent"}`}
              data-testid={`claim-type-filter-${t.name.toLowerCase()}`}>{t.name}</button>
          ))}
        </Swipeable>

        {/* Stage pills */}
        <Swipeable outerClassName="mb-4" className="flex items-center gap-2 pb-2 -mx-3 px-3 md:mx-0 md:px-0">
          <span className="text-[#3A4557] text-xs shrink-0">Stages:</span>
          <button onClick={() => setStageFilter("all")}
            className={`px-2.5 py-1 rounded-full text-xs font-medium shrink-0 border ${stageFilter === "all" ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "bg-[rgba(20,33,61,0.06)] text-[#44506B] border-[rgba(20,33,61,0.12)]"}`}>
            All <span className="font-bold">{tabCounts[claimTab]}</span>
          </button>
          {visibleStages.map((s) => {
            const active = stageFilter === s.id;
            const tc = getContrastTextColor(s.color);
            return (
              <button key={s.id} onClick={() => setStageFilter(active ? "all" : s.id)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 border"
                style={{ backgroundColor: active ? s.color : "rgba(20,33,61,0.06)", color: active ? tc : "#44506B", borderColor: active ? s.color : "rgba(20,33,61,0.12)" }}
                data-testid={`claim-stage-pill-${s.id}`}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                <span className="truncate max-w-[110px]">{s.name}</span>
                <span className="font-bold">{countByStage(s.id)}</span>
              </button>
            );
          })}
        </Swipeable>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
          <Input placeholder="Search by name, policy no. or broker..." value={search} onChange={(e) => setSearch(e.target.value)}
            className={`pl-8 h-9 text-sm ${inputCls}`} data-testid="claim-search-input" />
        </div>

        {/* List */}
        <div className="bg-white/50 border border-[#C9D4E6] rounded-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-[#C9D4E6]">
            <h3 className="text-[#1B2A4A] font-semibold text-sm">
              {stageFilter === "all" ? `${CLAIM_TABS.find((t) => t.id === claimTab)?.label} Claims` : getStage(stageFilter).name}
              <span className="text-[#3A4557] font-normal"> ({filtered.length})</span>
            </h3>
          </div>
          <div className="divide-y divide-[#C9D4E6]/50">
            {filtered.length === 0 ? (
              <div className="text-center py-10">
                <Hospital className="h-10 w-10 text-[#3A4557] mx-auto mb-2" />
                <p className="text-[#3A4557] text-sm">
                  {claimTab === "active" ? "No active claims" : claimTab === "approved" ? "No approved claims yet" : "No rejected claims"}
                </p>
                {claimTab === "active" && (
                  <Button onClick={openAdd} size="sm" className="mt-3 bg-green-600 hover:bg-green-700 text-white" data-testid="add-claim-empty-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Claim
                  </Button>
                )}
              </div>
            ) : (
              filtered.map((c) => {
                const st = getStage(c.stage);
                const tc = getContrastTextColor(st.color);
                return (
                  <div key={c.id} className="flex items-center gap-2 px-3 py-2.5 hover:bg-[#EDF1F8]/40" data-testid={`claim-row-${c.id}`}>
                    <button onClick={() => openEdit(c)} className="flex items-center gap-2.5 min-w-0 flex-1 text-left" data-testid={`claim-open-${c.id}`}>
                      <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0 border border-[#C9D4E6]" style={{ backgroundColor: st.color + "30" }}>
                        <span className="font-semibold text-sm text-[#1B2A4A]">{c.claimant_name?.charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-[#1B2A4A] font-medium text-sm truncate max-w-[160px]">{c.claimant_name}</p>
                          {c.claim_type && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-700 whitespace-nowrap">{c.claim_type}</span>}
                          {c.hospitalization_type && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-sky-500/15 text-sky-700 whitespace-nowrap">{c.hospitalization_type}</span>}
                        </div>
                        <p className="text-[#3A4557] text-xs truncate">
                          {fmtAmount(c.claim_amount)}{c.broker ? ` · ${c.broker}` : ""}{c.policy_number ? ` · ${c.policy_number}` : ""}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Select value={c.stage} onValueChange={(v) => handleStageChange(c.id, v)}>
                        <SelectTrigger className="h-6 min-w-[100px] max-w-[150px] border-0 text-[10px] font-medium rounded-full px-2"
                          style={{ backgroundColor: st.color, color: tc }} data-testid={`claim-stage-select-${c.id}`}>
                          <span style={{ color: tc }} className="truncate">{st.name}</span>
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#C9D4E6]">
                          {stages.map((s) => (
                            <SelectItem key={s.id} value={s.id} className="text-xs">
                              <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} /><span className="text-[#1B2A4A]">{s.name}</span></div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded-md hover:bg-[#EDF1F8] text-[#2E3B57]" title="Edit" data-testid={`claim-edit-btn-${c.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </main>

      {/* Add / Edit Claim Modal */}
      <Dialog open={showModal} onOpenChange={(o) => { setShowModal(o); if (!o) { setEditingId(null); setShowNameSuggest(false); } }}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="claim-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              {editingId ? <Pencil className="h-5 w-5 text-green-600" /> : <Plus className="h-5 w-5 text-green-600" />}
              {editingId ? "Edit Claim" : "Add Claim"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3.5 mt-3">
            {/* AI extract from letter */}
            <div className="rounded-xl border-2 border-dashed border-green-500/40 bg-green-500/5 p-3">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-green-600" /> Auto-fill from letter</p>
                  <p className="text-[11px] text-[#3A4557]">Upload the cashless approval or discharge letter — AI fills the details below.</p>
                </div>
                <label className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-semibold ${extracting ? "bg-green-600/60 cursor-wait" : "bg-green-600 hover:bg-green-700 cursor-pointer"}`} data-testid="claim-extract-label">
                  {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                  {extracting ? "Reading…" : "Upload"}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" disabled={extracting}
                    onChange={(e) => { handleExtractLetter(e.target.files?.[0]); e.target.value = ""; }} data-testid="claim-extract-input" />
                </label>
              </div>
              {editingId && form.claim_document_path && (
                <button type="button" onClick={handleViewLetter} className="mt-2 text-[11px] font-medium text-green-700 hover:underline flex items-center gap-1" data-testid="claim-view-letter-btn">
                  <FileText className="h-3.5 w-3.5" /> View uploaded letter
                </button>
              )}
            </div>

            {/* Claimant name with autocomplete */}
            <div className="relative" ref={nameBoxRef}>
              <Label className="text-[#3A4557] text-xs">Claimant Name *</Label>
              <Input
                value={form.claimant_name}
                onChange={(e) => { setForm({ ...form, claimant_name: e.target.value }); setShowNameSuggest(true); }}
                onFocus={() => setShowNameSuggest(true)}
                onBlur={() => setTimeout(() => setShowNameSuggest(false), 150)}
                placeholder="Type a name (suggests from your clients)"
                className={`mt-1 ${inputCls}`}
                data-testid="claim-name-input"
                autoComplete="off"
              />
              {showNameSuggest && nameMatches.length > 0 && (
                <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#C9D4E6] rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="claim-name-suggestions">
                  {nameMatches.map((e, idx) => (
                    <button key={idx} type="button" onMouseDown={(ev) => { ev.preventDefault(); pickSuggestion(e); }}
                      className="w-full text-left px-3 py-2 hover:bg-[#EDF1F8] border-b border-[#EDF1F8] last:border-0"
                      data-testid={`claim-name-suggestion-${idx}`}>
                      <p className="text-sm text-[#1B2A4A] font-medium">{e.name}</p>
                      <p className="text-[11px] text-[#3A4557]">
                        {[e.insurer, e.broker, e.policy_number].filter(Boolean).join(" · ") || "No policy on record"}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Broker with datalist suggestions */}
              <div>
                <Label className="text-[#3A4557] text-xs flex items-center gap-1"><Building2 className="h-3 w-3" />Broker</Label>
                <Input list="claim-brokers" value={form.broker} onChange={(e) => setForm({ ...form, broker: e.target.value })}
                  placeholder="e.g. Insurance Dekho" className={`mt-1 ${inputCls}`} data-testid="claim-broker-input" />
                <datalist id="claim-brokers">
                  {lookup.brokers.map((b) => <option key={b} value={b} />)}
                </datalist>
              </div>
              <div>
                <Label className="text-[#3A4557] text-xs">Insurer</Label>
                <Input value={form.insurer} onChange={(e) => setForm({ ...form, insurer: e.target.value })}
                  placeholder="e.g. Star Health" className={`mt-1 ${inputCls}`} data-testid="claim-insurer-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#3A4557] text-xs flex items-center gap-1"><IndianRupee className="h-3 w-3" />Claim Amount</Label>
                <Input value={form.claim_amount} onChange={(e) => setForm({ ...form, claim_amount: e.target.value })}
                  placeholder="e.g. 150000" className={`mt-1 ${inputCls}`} data-testid="claim-amount-input" />
              </div>
              <div>
                <Label className="text-[#3A4557] text-xs">Policy Number</Label>
                <Input value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
                  placeholder="Policy no." className={`mt-1 ${inputCls}`} data-testid="claim-policy-input" />
              </div>
            </div>

            <div>
              <Label className="text-[#3A4557] text-xs flex items-center gap-1"><IndianRupee className="h-3 w-3" />Settled / Approved Amount</Label>
              <Input value={form.settled_amount} onChange={(e) => setForm({ ...form, settled_amount: e.target.value })}
                placeholder="Fill once the claim is approved" className={`mt-1 ${inputCls}`} data-testid="claim-settled-input" />
              <p className="text-[10px] text-[#3A4557] mt-1">The final amount the insurer settled (can differ from the claim amount).</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#3A4557] text-xs">Claim Type</Label>
                <Select value={form.claim_type} onValueChange={(v) => setForm({ ...form, claim_type: v })}>
                  <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="claim-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6]">
                    {claimTypes.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#3A4557] text-xs">Hospitalization</Label>
                <Select value={form.hospitalization_type} onValueChange={(v) => setForm({ ...form, hospitalization_type: v })}>
                  <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="claim-hosp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6]">
                    {hospTypes.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[#3A4557] text-xs">Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="claim-stage-modal-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
                <SelectContent className="bg-white border-[#C9D4E6]">
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />{s.name}</div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#3A4557] text-xs">Hospital Name</Label>
                <Input value={form.hospital_name} onChange={(e) => setForm({ ...form, hospital_name: e.target.value })} placeholder="Hospital" className={`mt-1 ${inputCls}`} data-testid="claim-hospital-input" />
              </div>
              <div>
                <Label className="text-[#3A4557] text-xs">Claim Ref No.</Label>
                <Input value={form.claim_ref_no} onChange={(e) => setForm({ ...form, claim_ref_no: e.target.value })} placeholder="Reference no." className={`mt-1 ${inputCls}`} data-testid="claim-ref-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#3A4557] text-xs">Admission Date</Label>
                <Input type="date" value={form.admission_date} onChange={(e) => setForm({ ...form, admission_date: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="claim-admission-input" />
              </div>
              <div>
                <Label className="text-[#3A4557] text-xs">Discharge Date</Label>
                <Input type="date" value={form.discharge_date} onChange={(e) => setForm({ ...form, discharge_date: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="claim-discharge-input" />
              </div>
            </div>

            <div>
              <Label className="text-[#3A4557] text-xs">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" className={`mt-1 ${inputCls}`} data-testid="claim-notes-input" />
            </div>

            <div className="flex gap-3 pt-1">
              {editingId && (
                <Button variant="outline" onClick={handleDelete} disabled={saving} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="claim-delete-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-[#C9D4E6]">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="claim-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : editingId ? <Check className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                {editingId ? "Save" : "Add"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettings} onOpenChange={(o) => { setShowSettings(o); if (!o) setEditingStageId(null); }}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-md max-h-[85vh] overflow-y-auto" data-testid="claim-settings-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2"><Settings className="h-5 w-5 text-green-600" />Customize Claims</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-3">
            {/* Stages */}
            <div>
              <Label className="text-[#3A4557] text-xs flex items-center gap-2 mb-2"><BadgeCheck className="h-4 w-4" />Stages</Label>
              <div className="space-y-2 mb-3">
                {stages.map((s) => (
                  <div key={s.id} className="p-2 bg-[#EDF1F8]/50 rounded-lg" data-testid={`claim-stage-setting-${s.id}`}>
                    {editingStageId === s.id ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <input type="color" value={editStageColor} onChange={(e) => setEditStageColor(e.target.value)} className="h-9 w-9 rounded cursor-pointer border border-[#C9D4E6]" />
                          <Input value={editStageName} onChange={(e) => setEditStageName(e.target.value)} className="flex-1 h-9 text-sm bg-white border-[#C9D4E6]" autoFocus />
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingStageId(null)} className="flex-1 h-8 border-[#C9D4E6]">Cancel</Button>
                          <Button size="sm" onClick={() => saveStageEdit(s.id)} className="flex-1 h-8 bg-green-600 text-white">Save</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="h-4 w-4 rounded border border-[#C9D4E6]" style={{ backgroundColor: s.color }} />
                        <span className="text-[#1B2A4A] text-sm flex-1 truncate">{s.name}</span>
                        <button onClick={() => { setEditingStageId(s.id); setEditStageName(s.name); setEditStageColor(s.color || "#3b82f6"); }} className="text-[#2E3B57] p-1 rounded hover:bg-[#EDF1F8]" data-testid={`claim-stage-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deleteStage(s.id)} className="text-red-600 p-1 rounded hover:bg-red-500/10" data-testid={`claim-stage-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input type="color" value={newStageColor} onChange={(e) => setNewStageColor(e.target.value)} className="h-9 w-9 rounded cursor-pointer border border-[#C9D4E6]" />
                <Input value={newStageName} onChange={(e) => setNewStageName(e.target.value)} placeholder="New stage..." onKeyPress={(e) => e.key === "Enter" && addStage()} className="flex-1 h-9 text-sm bg-[#EDF1F8] border-[#C9D4E6]" data-testid="claim-new-stage-input" />
                <Button size="sm" onClick={addStage} className="h-9 bg-green-600 text-white" data-testid="claim-add-stage-btn"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>

            {/* Claim types */}
            <div>
              <Label className="text-[#3A4557] text-xs flex items-center gap-2 mb-2"><FileText className="h-4 w-4" />Claim Types</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {claimTypes.map((t) => (
                  <span key={t.id} className="flex items-center gap-1 bg-emerald-500/15 text-emerald-700 text-xs px-2 py-1 rounded border border-emerald-500/30">
                    {t.name}<button onClick={() => deleteType(t.id)} className="text-red-600 ml-1"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newType} onChange={(e) => setNewType(e.target.value)} placeholder="Add claim type..." onKeyPress={(e) => e.key === "Enter" && addType()} className="flex-1 h-8 text-sm bg-[#EDF1F8] border-[#C9D4E6]" data-testid="claim-new-type-input" />
                <Button size="sm" onClick={addType} className="h-8 bg-emerald-600 text-white" data-testid="claim-add-type-btn"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>

            {/* Hospitalization types */}
            <div>
              <Label className="text-[#3A4557] text-xs flex items-center gap-2 mb-2"><Hospital className="h-4 w-4" />Hospitalization Types</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {hospTypes.map((t) => (
                  <span key={t.id} className="flex items-center gap-1 bg-sky-500/15 text-sky-700 text-xs px-2 py-1 rounded border border-sky-500/30">
                    {t.name}<button onClick={() => deleteHosp(t.id)} className="text-red-600 ml-1"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newHosp} onChange={(e) => setNewHosp(e.target.value)} placeholder="Add hospitalization type..." onKeyPress={(e) => e.key === "Enter" && addHosp()} className="flex-1 h-8 text-sm bg-[#EDF1F8] border-[#C9D4E6]" data-testid="claim-new-hosp-input" />
                <Button size="sm" onClick={addHosp} className="h-8 bg-sky-600 text-white" data-testid="claim-add-hosp-btn"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Analytics Modal */}
      <Dialog open={showAnalytics} onOpenChange={setShowAnalytics}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="claim-analytics-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2"><BarChart3 className="h-5 w-5 text-green-600" />Claim Analytics</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-[#3A4557]">Financial Year</span>
              <Select value={analyticsFy} onValueChange={(v) => { setAnalyticsFy(v); loadAnalytics(v); }}>
                <SelectTrigger className="h-8 w-44 bg-[#EDF1F8] border-[#C9D4E6] text-sm text-[#1B2A4A]" data-testid="analytics-fy-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[#C9D4E6]">
                  <SelectItem value="all">All time</SelectItem>
                  {(analytics?.fy_list || []).map((y) => <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {analyticsLoading || !analytics ? (
              <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#A87608]" /></div>
            ) : (
              <div className="space-y-5" data-testid="analytics-content">
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-xl border border-[#C9D4E6] bg-[#EDF1F8]/50 p-3 text-center">
                    <p className="text-[10px] text-[#3A4557] uppercase tracking-wide">Total Claims</p>
                    <p className="text-xl font-bold text-[#1B2A4A]" data-testid="analytics-total-claims">{analytics.totals.count}</p>
                  </div>
                  <div className="rounded-xl border border-[#C9D4E6] bg-amber-500/10 p-3 text-center">
                    <p className="text-[10px] text-[#3A4557] uppercase tracking-wide">Claimed</p>
                    <p className="text-base font-bold text-[#A87608]" data-testid="analytics-total-claimed">{fmtINR(analytics.totals.claim_amount)}</p>
                  </div>
                  <div className="rounded-xl border border-[#C9D4E6] bg-emerald-500/10 p-3 text-center">
                    <p className="text-[10px] text-[#3A4557] uppercase tracking-wide">Settled</p>
                    <p className="text-base font-bold text-emerald-700" data-testid="analytics-total-settled">{fmtINR(analytics.totals.settled_amount)}</p>
                  </div>
                </div>
                <AnalyticsSection title="By Stage" rows={analytics.by_stage} total={analytics.totals.count} />
                <AnalyticsSection title="Cashless vs Reimbursement" rows={analytics.by_type} total={analytics.totals.count} />
                <AnalyticsSection title="Hospitalization Type" rows={analytics.by_hosp} total={analytics.totals.count} />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
}
